import { type SelectionRange, type GridPosition } from '../../../state/types';

/**
 * Computes the bounding range for a fill (drag-to-fill) operation.
 *
 * The fill range extends the source selection vertically to include the
 * target cell's row, while keeping columns strictly locked to the source.
 * This means users can only fill up or down — never sideways.
 *
 * When the target row falls inside the source bounds, the returned range
 * matches the original source (no-op visually). The source anchor/lead
 * orientation is normalized, so reverse-oriented selections are handled.
 */
export function calculateFillRange(
  source: SelectionRange,
  targetCell: GridPosition
): SelectionRange {
  const sourceMinRow = Math.min(source.anchor.row.index, source.lead.row.index);
  const sourceMaxRow = Math.max(source.anchor.row.index, source.lead.row.index);

  const sourceMinCol = Math.min(source.anchor.col.index, source.lead.col.index);
  const sourceMaxCol = Math.max(source.anchor.col.index, source.lead.col.index);

  const targetRow = targetCell.row.index;

  const fillAnchorRow = Math.min(sourceMinRow, targetRow);
  const fillLeadRow = Math.max(sourceMaxRow, targetRow);

  return {
    id: `fill-preview-${source.id}`,
    anchor: {
      row: { index: fillAnchorRow },
      col: { index: sourceMinCol }
    },
    lead: {
      row: { index: fillLeadRow },
      col: { index: sourceMaxCol }
    }
  };
}
